import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/auth";
import * as ella from "@/lib/ellaApi";
import { LOGO } from "@/lib/ellaApi";
import { GOOGLE_CLIENT_ID } from "@/lib/seo";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

// Decode a Google ID token (JWT) payload → { sub, name, email, ... }, UTF-8 safe.
function decodeJwt(token) {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64).split("").map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join("")
    );
    return JSON.parse(json);
  } catch { return {}; }
}

export default function AuthModal({ open, onClose, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode); // login | register | reset
  const { login, loginWithGoogle, register, loading } = useAuth();
  const [showPass, setShowPass] = useState(false);
  const googleBtnRef = useRef(null);

  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Register state
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPass, setRPass] = useState("");
  const [rPass2, setRPass2] = useState("");
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [regTerms, setRegTerms] = useState(null);

  // Reset state
  const [resetEmail, setResetEmail] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  // Load the active T&C content for the registration consent box (legacy parity)
  useEffect(() => {
    if (!open || mode !== "register" || regTerms) return;
    ella.getActiveTerms().then((res) => {
      if (res.status && res.data) setRegTerms(res.data);
    }).catch(() => {});
  }, [open, mode, regTerms]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) { toast.error("Isi email/no. handphone dan password"); return; }
    const r = await login(email, password);
    if (r.ok) { toast.success(`Selamat datang, ${r.data.Name}!`); onClose(); }
    else toast.error(r.message);
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    if (!rName || !rEmail || !rPass) { toast.error("Lengkapi data wajib"); return; }
    if (rPass !== rPass2) { toast.error("Password dan Konfirmasi Password tidak sama"); return; }
    if (!agreePrivacy) { toast.error("Mohon membaca dan menyetujui Kebijakan Privasi Ellafroze terlebih dahulu"); return; }
    const r = await register({ name: rName, email: rEmail, password: rPass });
    if (r.ok) { toast.success("Akun berhasil dibuat!"); onClose(); }
    else toast.error(r.message);
  };

  const handleReset = async (e) => {
    e?.preventDefault();
    if (!resetEmail) { toast.error("Isi email akunmu"); return; }
    setResetBusy(true);
    try {
      const res = await ella.doResetPassword(resetEmail);
      if (res.status) {
        toast.success(res.message || "Password baru sudah dikirim ke email kamu");
        setMode("login");
      } else {
        toast.error(res.message || "Gagal mereset password");
      }
    } catch {
      // upstream can 500 when its mail service is down
      toast.error("Gagal mereset password, silahkan coba lagi atau hubungi admin");
    } finally { setResetBusy(false); }
  };

  // Render the official Google Identity Services button once the GIS script has
  // loaded and the modal shows the login/register view. On sign-in, decode the
  // ID token and hand ID/Name/Email/TokenID to the backend (doAuthGoogle).
  useEffect(() => {
    if (!open || mode === "reset") return undefined;
    let stopped = false;
    const render = () => {
      const gid = window.google?.accounts?.id;
      if (!gid || !googleBtnRef.current) return false;
      gid.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          const p = decodeJwt(credential);
          if (!p.email) { toast.error("Gagal masuk dengan Google"); return; }
          const r = await loginWithGoogle({ id: p.sub, name: p.name, email: p.email, tokenId: credential });
          if (r.ok) { toast.success(`Selamat datang, ${r.data?.Name || p.name || ""}!`); onClose(); }
          else toast.error(r.message || "Login dengan Google gagal");
        },
      });
      googleBtnRef.current.innerHTML = "";
      gid.renderButton(googleBtnRef.current, { theme: "outline", size: "large", shape: "pill", text: "continue_with", width: 300 });
      return true;
    };
    if (render()) return undefined;
    const iv = setInterval(() => { if (stopped || render()) clearInterval(iv); }, 200); // GIS may still be loading
    const to = setTimeout(() => clearInterval(iv), 6000);
    return () => { stopped = true; clearInterval(iv); clearTimeout(to); };
  }, [open, mode, loginWithGoogle, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md sm:rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <img src={LOGO} alt="Ella Froze" className="h-12" />
          </div>
          <DialogTitle className="font-display text-2xl text-center">
            {mode === "login" ? "Masuk ke Akunmu" : mode === "register" ? "Buat Akun Baru" : "Reset Password"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {mode === "login" ? "Belanja frozen food favoritmu jadi lebih mudah"
              : mode === "register" ? "Daftar sekarang dan dapatkan penawaran menarik"
              : "Password baru akan dikirim ke email akunmu"}
          </DialogDescription>
        </DialogHeader>

        {mode === "login" && (
          <form className="space-y-3 mt-2" onSubmit={handleLogin}>
            <div>
              <Label htmlFor="login-email">Email / No. Handphone</Label>
              <Input id="login-email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@email.com atau 08xxxxxxxxxx" />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <div className="relative">
                <Input id="login-password" data-testid="login-password-input" type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPass ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
              </div>
              <div className="text-right mt-1">
                <button type="button" onClick={() => setMode("reset")} className="text-xs text-[#D32F2F] font-medium" data-testid="forgot-password-link">Lupa password?</button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-primary h-11" data-testid="login-submit-button">
              {loading ? "Memuat..." : "Masuk"}
            </Button>
          </form>
        )}

        {mode === "register" && (
          <form className="space-y-3 mt-2" onSubmit={handleRegister}>
            <div>
              <Label htmlFor="reg-name">Nama Lengkap</Label>
              <Input id="reg-name" data-testid="register-name-input" value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Nama lengkap" />
            </div>
            <div>
              <Label htmlFor="reg-email">Email / No. Handphone</Label>
              <Input id="reg-email" data-testid="register-email-input" value={rEmail} onChange={(e) => setREmail(e.target.value)} placeholder="nama@email.com atau 08xxxxxxxxxx" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="reg-pass">Password</Label>
                <Input id="reg-pass" data-testid="register-password-input" type="password" value={rPass} onChange={(e) => setRPass(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="reg-pass2">Konfirmasi</Label>
                <Input id="reg-pass2" data-testid="register-password-confirm-input" type="password" value={rPass2} onChange={(e) => setRPass2(e.target.value)} />
              </div>
            </div>

            {/* PDP / privacy policy consent box (same content as the legacy register screen) */}
            <div className="max-h-44 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700 leading-relaxed" data-testid="register-terms-box">
              <p className="font-bold">{regTerms?.Title || "KEBIJAKAN PRIVASI ELLAFROZE"}</p>
              {regTerms?.Version && <p className="text-[10px] text-gray-500 mb-1">Versi {regTerms.Version}</p>}
              <div className="whitespace-pre-line">{regTerms?.Content || "Memuat Kebijakan Privasi Ellafroze..."}</div>
              <p className="mt-2">
                <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#D32F2F] underline">Baca Kebijakan Privasi lengkap</a>
              </p>
            </div>
            <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
              <Checkbox checked={agreePrivacy} onCheckedChange={setAgreePrivacy} data-testid="privacy-consent-checkbox" className="mt-0.5" />
              <span>
                Saya dengan ini menyatakan telah membaca dan memahami Kebijakan Privasi Ellafroze serta memberikan persetujuan secara sadar dan eksplisit kepada Ellafroze untuk memproses data pribadi saya sesuai Kebijakan Privasi.
              </span>
            </label>
            <Button type="submit" disabled={loading} className="w-full btn-primary h-11" data-testid="register-submit-button">
              {loading ? "Mendaftarkan..." : "Daftar"}
            </Button>
          </form>
        )}

        {mode === "reset" && (
          <form className="space-y-3 mt-2" onSubmit={handleReset}>
            <div>
              <Label htmlFor="reset-email">Email</Label>
              <Input id="reset-email" data-testid="reset-email-input" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="nama@email.com" />
            </div>
            <Button type="submit" disabled={resetBusy} className="w-full btn-primary h-11" data-testid="reset-submit-button">
              {resetBusy ? "Mengirim..." : "Kirim Password Baru"}
            </Button>
            <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-gray-500" data-testid="reset-back-to-login">Kembali ke halaman masuk</button>
          </form>
        )}

        {mode !== "reset" && (
          <>
            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">atau</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            {/* Official Google Identity Services button (rendered into this container) */}
            <div className="flex justify-center min-h-[44px]" ref={googleBtnRef} data-testid="google-signin-button" />
          </>
        )}

        <p className="text-center text-sm text-gray-600 mt-2">
          {mode === "login" ? (
            <>Belum punya akun?{" "}
              <button onClick={() => setMode("register")} className="text-[#D32F2F] font-semibold" data-testid="switch-to-register">Daftar disini</button>
            </>
          ) : mode === "register" ? (
            <>Sudah punya akun?{" "}
              <button onClick={() => setMode("login")} className="text-[#D32F2F] font-semibold" data-testid="switch-to-login">Masuk disini</button>
            </>
          ) : null}
        </p>
      </DialogContent>
    </Dialog>
  );
}
